// ABOUTME: Guards metadata-defined routes with the current authenticated user and route permissions.
// ABOUTME: Preserves a bounded MCP consent interaction when directing the user through login.
import React, { useState } from "react";
import { useAuthState, useAuthorization } from "_ReactContext/AuthContext";
import { navigate } from "@gatsbyjs/reach-router";
import "i18n/i18n";
import { i18f, setTranslationPathSource } from "i18n/i18n";
import PageNotAuthorized from "Pages/_General/PageNotAuthorized";
import { checkTokenExpiration } from "./utils";
import ModalSessionExpired from "./ModalSessionExpired";
import useIdleTimer from "./useIdleTimer";
import { mcpLoginReturnTarget } from "Pages/Mcp/consent-state";
import HelperLoading from "Components/Loading/HelperLoading";

const Authenticated = (props: any) => {
    const {
        isAuthenticated,
        company,
        companyLoaded,
        companyLoadFailed
    } = useAuthState();
    const { isRouteAuthorized } = useAuthorization();
    const [pathToRedirect, setPathToRedirect] = React.useState("");
    const plan = company.plan || "noplan";
    const companyId = company.companyId || 0;
    const inactivityTimeoutMinutes = company.inactivityTimeoutMinutes;
    const [showModal, setShowModal] = useState(false);
    const [readyTranslationPath, setReadyTranslationPath] = useState(() =>
        localStorage.getItem("i18nPath")
    );
    const translationPath = `${
        import.meta.env.VITE_API_ADDRESS
    }/general/translation/${companyId}/0`;
    const canChangeTranslationPath =
        (companyId > 0 && plan !== "noplan") ||
        (companyId === 0 && plan === "noplan");
    // A restored session already has a company; keep it usable while the query refreshes.
    const waitingForCompany = !companyLoaded && !companyLoadFailed;
    const translationSource = companyLoadFailed
        ? "authenticated_company_failed"
        : companyId === 0
          ? "authenticated_zero_company"
          : "authenticated_ready";

    useIdleTimer({
        idleTimeoutMinutes: inactivityTimeoutMinutes
    });
    React.useEffect(() => {
        if (
            !isAuthenticated ||
            waitingForCompany ||
            !canChangeTranslationPath
        ) {
            return;
        }
        if (translationPath !== localStorage.getItem("i18nPath")) {
            i18f(translationPath, translationSource);
        }
        setReadyTranslationPath(translationPath);
    }, [
        isAuthenticated,
        waitingForCompany,
        canChangeTranslationPath,
        translationPath,
        translationSource
    ]);

    React.useEffect(() => {
        if (pathToRedirect !== "") {
            navigate(pathToRedirect);
        }
    }, [pathToRedirect]);

    // Temporary removed to avoid any issues with date errors on token verification
    // and show the modal incorrectly
    // React.useEffect(() => {
    //     const intervalId = setInterval(async () => {
    //         const isTokenExpired = await checkTokenExpiration({
    //             tryAgainError: { tryAgain: true, tryAgainInSeconds: 15 }
    //         });
    //         setShowModal(isTokenExpired);
    //     }, 20 * 1000); // Check every 20 seconds, adjust as needed
    //     return () => clearInterval(intervalId);
    // }, []);

    const currentRoute = props.routes.find(
        (item: any) => item.route === props.path
    );

    if (isAuthenticated) {
        if (
            waitingForCompany ||
            (canChangeTranslationPath &&
                readyTranslationPath !== translationPath)
        ) {
            return <HelperLoading />;
        }
        // Written during render on purpose: children can request translation
        // namespaces while rendering, before any effect runs, and the generic-path
        // diagnostic must attribute them to this transition.
        setTranslationPathSource(translationSource);
        let routeAuthorized = true;

        if (
            currentRoute?.uniqueName === "mcpconnections" &&
            company.mcpEnabled !== true
        ) {
            routeAuthorized = false;
        }

        if (
            routeAuthorized &&
            currentRoute !== undefined &&
            currentRoute.uniqueName !== undefined
        ) {
            let requiresAuthorization = true;
            if (
                currentRoute.requiresAuthorization !== undefined &&
                !currentRoute.requiresAuthorization
            ) {
                requiresAuthorization = false;
            }
            if (requiresAuthorization) {
                routeAuthorized = isRouteAuthorized(currentRoute.uniqueName);
            }
        }

        if (routeAuthorized) {
            return (
                <>
                    {showModal && (
                        <ModalSessionExpired
                            open={showModal}
                            setOpen={setShowModal}
                        />
                    )}
                    {React.cloneElement(props.children, {
                        ...props
                    })}
                </>
            );
        } else {
            return <PageNotAuthorized />;
        }
    } else {
        let redirectAddress = "/";
        if (currentRoute !== undefined && currentRoute.route !== undefined) {
            redirectAddress =
                mcpLoginReturnTarget(
                    window.location.pathname,
                    window.location.search
                ) || encodeURI(window.location.pathname);
        }
        if (pathToRedirect === "") {
            setPathToRedirect(`/login?to=${redirectAddress}`);
        }
        return null;
    }
};

export default Authenticated;
